import { useFormikContext } from 'formik';
import { isNil, keyBy, getOr } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '..';
import SelectField from '../../../../../components/fields/SelectField';
import CollapsibleWrapper, { Panel } from '../../../../../components/wrappers/CollapsibleWrapper';
import { getWorkbookToExport, exportFinancialTabsExcel } from '../../../../../utilities/excel';
import { useFinanceProductDetailContext } from '../../FinanceProductDetailContext';
import ExportExcel from '../../shared/ExportExcel';
import ImportExcel from '../../shared/ImportExcel';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import useTerms from './LeaseReferenceTable/useTerms';
import TermValueTable from './TermValueTable';

const mappedHeaders = {
    'Term*': 'term',
    'Downpayment*': 'value',
};

export type DownPaymentTableProps = {
    disabled?: boolean;
};

const DownPaymentTable = ({ disabled = false }: DownPaymentTableProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();
    const { bank } = useFinanceProductDetailContext();

    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);

    const {
        displayName,
        downPayment: { table: downPaymentTable },
        term: termSettings,
    } = values;

    const terms = useTerms(termSettings);

    const onImport = useCallback(
        async data => {
            const tableDataList = data
                .filter(record => !isNil(record.value))
                .map(value => ({
                    term: value.term,
                    value: value.value,
                }));

            setFieldValue('downPayment.table', tableDataList);
        },
        [setFieldValue]
    );

    const exportExcel = useCallback(async () => {
        const workbook = await getWorkbookToExport(mappedHeaders);
        const worksheet = workbook.getWorksheet(1);
        const downPaymentTableByTerm = keyBy('term', downPaymentTable || []);

        terms.forEach(term => {
            worksheet.addRow({ term, value: getOr(null, `${term}.value`, downPaymentTableByTerm) });
        });

        exportFinancialTabsExcel(workbook, displayName, 'Downpayment');
    }, [displayName, terms, downPaymentTable]);

    return (
        <CollapsibleWrapper defaultActiveKey={['downPayment']}>
            <Panel key="downPayment" header={t('financeProductDetails:tables.downpayment.title')}>
                <TermValueTable
                    disabled={disabled}
                    settingsKey="downPayment"
                    valueTitle={
                        <>
                            {t('financeProductDetails:tables.downpayment.title')}
                            <SelectField
                                itemProps={{ noStyle: true }}
                                name="downPayment.unit"
                                options={amountUnitOptions}
                                showSearch
                            />
                        </>
                    }
                />
            </Panel>
            <ImportExcel
                mappedHeaders={mappedHeaders}
                onImport={onImport}
                title={t('financeProductDetails:importModal.titles.downpayment')}
            />
            <ExportExcel onClick={exportExcel} />
        </CollapsibleWrapper>
    );
};

export default DownPaymentTable;
